import { get, httpDelete, post, put } from 'api/client';
import env from 'config';
import Logger from 'utils/logger';
import {
  Report,
  ReportAutomation,
  ReportExport,
  ReportExportFormat,
  SerializedReportData,
} from 'types/Report';
import RequestHeaders from 'app/constants/requestHeaders';

const NGROK_HEADERS = {
  'ngrok-skip-browser-warning': 'true',
} as const;

type ReportSortBy = 'name' | 'users.name' | 'created_at';
type SortDirection = 'asc' | 'desc';

const VALID_SORT_VALUES: ReportSortBy[] = ['name', 'users.name', 'created_at'];
const VALID_SORT_DIRECTIONS: SortDirection[] = ['asc', 'desc'];

function serializeSortByValues(name: string): ReportSortBy | undefined {
  if (name === 'users_name') {
    return 'users.name';
  }

  if (name === 'user_id' || name === 'profiles') {
    return 'users.name';
  }

  if (VALID_SORT_VALUES.includes(name as ReportSortBy)) {
    return name as ReportSortBy;
  }

  return undefined;
}

function serializeSortDirection(direction: string): SortDirection | undefined {
  if (VALID_SORT_DIRECTIONS.includes(direction as SortDirection)) {
    return direction as SortDirection;
  }

  return undefined;
}

const ReportApi = {
  getAll(params: {
    page?: number;
    per_page?: number;
    search?: string;
    sort_by?: string;
    sort_dir?: string;
  }): Promise<{
    data: Report[];
    meta: {
      total: number;
      current_page: number;
      per_page: number;
      last_page: number;
    };
  }> {
    return get(`/api/reports`, {
      baseURL: env.REPORTS_API_ENDPOINT,
      params: {
        ...params,
        sort_by: params.sort_by
          ? serializeSortByValues(params.sort_by)
          : undefined,
        sort_dir: params.sort_dir
          ? serializeSortDirection(params.sort_dir)
          : undefined,
      },
      headers: NGROK_HEADERS,
    });
  },
  detail(
    reportId: number,
    jwtToken?: string,
    accountId?: number,
  ): Promise<Report> {
    return get(`/api/reports/${reportId}`, {
      baseURL: env.REPORTS_API_ENDPOINT,
      headers: {
        ...NGROK_HEADERS,
        ...(jwtToken && {
          [RequestHeaders.Authorization]: `Bearer ${jwtToken}`,
        }),
        ...(accountId !== undefined && {
          [RequestHeaders.AccountId]: String(accountId),
        }),
      },
    });
  },
  async create(report: SerializedReportData): Promise<Report> {
    const createdReport: Report = await post(`/api/reports`, report, {
      baseURL: env.REPORTS_API_ENDPOINT,
      headers: NGROK_HEADERS,
    });

    this.createExport(createdReport.id).catch((error) => {
      Logger.warn('Failed to trigger export generation:', error);
    });

    return createdReport;
  },
  delete(reportId: number): Promise<Report> {
    return httpDelete(`/api/reports/${reportId}`, {
      baseURL: env.REPORTS_API_ENDPOINT,
      headers: NGROK_HEADERS,
    });
  },
  edit(report: SerializedReportData & { id: number }): Promise<Report> {
    return put(`/api/reports/${report.id}`, report, {
      baseURL: env.REPORTS_API_ENDPOINT,
      headers: NGROK_HEADERS,
    });
  },
  async duplicate({ id, name }: { id: number; name: string }): Promise<Report> {
    const duplicatedReport: Report = await post(
      `/api/reports/${id}/duplicate`,
      {
        name,
      },
      {
        baseURL: env.REPORTS_API_ENDPOINT,
        headers: NGROK_HEADERS,
      },
    );

    this.createExport(duplicatedReport.id).catch((error) => {
      Logger.warn(
        'Failed to trigger export generation for duplicated report:',
        error,
      );
    });

    return duplicatedReport;
  },
  createExport(
    reportId: number,
    format: ReportExportFormat = 'pdf',
  ): Promise<Report> {
    return post(
      `/api/reports/${reportId}/exports/${format}`,
      {},
      {
        baseURL: env.REPORTS_API_ENDPOINT,
        headers: NGROK_HEADERS,
      },
    );
  },
  getExport(
    reportId: number,
    format: ReportExportFormat = 'pdf',
  ): Promise<ReportExport> {
    return get(`/api/reports/${reportId}/exports/${format}`, {
      baseURL: env.REPORTS_API_ENDPOINT,
      headers: NGROK_HEADERS,
    });
  },

  async createAutomation(
    reportId: number,
    data: { day_offset: number },
  ): Promise<ReportAutomation> {
    const payload = {
      report_id: reportId,
      periodicity: 'monthly' as const,
      month_day: data.day_offset,
    };
    return post(`/api/schedules`, payload, {
      baseURL: env.REPORTS_API_ENDPOINT,
      headers: NGROK_HEADERS,
    });
  },

  async getAutomation(reportId: number): Promise<ReportAutomation | null> {
    const report = await this.detail(reportId);
    return report.automation || null;
  },

  async updateAutomation(
    reportId: number,
    data: { day_offset: number },
  ): Promise<ReportAutomation> {
    const report = await this.detail(reportId);
    const scheduleId = report.automation?.id;
    if (!scheduleId) {
      throw new Error('No automation found for this report');
    }
    const payload = {
      report_id: reportId,
      periodicity: 'monthly' as const,
      month_day: data.day_offset,
    };
    return put(`/api/schedules/${scheduleId}`, payload, {
      baseURL: env.REPORTS_API_ENDPOINT,
      headers: NGROK_HEADERS,
    });
  },

  async deleteAutomation(reportId: number): Promise<void> {
    const report = await this.detail(reportId);
    const scheduleId = report.automation?.id;
    if (!scheduleId) {
      throw new Error('No automation found for this report');
    }
    return httpDelete(`/api/schedules/${scheduleId}`, {
      baseURL: env.REPORTS_API_ENDPOINT,
      headers: NGROK_HEADERS,
    });
  },
};

export default ReportApi;
